import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Video, CheckCircle2, Camera, ShieldCheck, Clock, Lightbulb, Mail, Sparkles, Eye, Inbox, Home } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function CandidatePortal() {
  const { inviteId } = useParams();
  const [data, setData] = useState(null);
  const [step, setStep] = useState("welcome"); // welcome | setup | interview | done
  const [qIdx, setQIdx] = useState(0);
  const [thinkLeft, setThinkLeft] = useState(0);
  const [answerLeft, setAnswerLeft] = useState(0);
  const [recording, setRecording] = useState(false);
  const [stream, setStream] = useState(null);
  const [recorded, setRecorded] = useState(false);
  const videoRef = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const recordStartAt = useRef(null);
  const qStarts = useRef([]);  // seconds offset from start of recording, per question
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get(`/interview/${inviteId}`).then(r => {
      setData(r.data);
      if (r.data.submitted) setStep("done");
    }).catch(() => toast.error("Invalid invitation"));
  }, [inviteId]);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(s);
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.muted = true; }
    } catch (e) { toast.error("Camera/mic permission denied"); }
  };

  useEffect(() => {
    if (step === "setup" || step === "interview") startCamera();
    return () => { stream && stream.getTracks().forEach(t => t.stop()); };
    // eslint-disable-next-line
  }, [step]);

  useEffect(() => {
    if (step !== "interview" || !data) return;
    const q = data.questions[qIdx];
    setThinkLeft(q.think_time); setAnswerLeft(q.answer_time); setRecorded(false);
    const t1 = setInterval(() => setThinkLeft((v) => v - 1), 1000);
    return () => clearInterval(t1);
  }, [step, qIdx, data]);

  useEffect(() => {
    if (thinkLeft === 0 && step === "interview" && !recording && !recorded) startRecording();
    // For questions 2+, recording is already active — mark this question's start offset now.
    if (thinkLeft === 0 && step === "interview" && recording && qIdx > 0 && qStarts.current[qIdx] == null) {
      const offset = recordStartAt.current ? (Date.now() - recordStartAt.current) / 1000 : 0;
      qStarts.current[qIdx] = Math.round(offset * 10) / 10;
    }
    // eslint-disable-next-line
  }, [thinkLeft]);

  useEffect(() => {
    if (!recording) return;
    if (answerLeft <= 0) { stopRecording(); return; }
    const t = setInterval(() => setAnswerLeft(v => v - 1), 1000);
    return () => clearInterval(t);
  }, [recording, answerLeft]);

  const startRecording = () => {
    if (!stream) return;
    chunks.current = [];
    const mr = new MediaRecorder(stream, { mimeType: "video/webm" });
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
    mr.onstop = () => setRecorded(true);
    mr.start();
    mediaRecorder.current = mr;
    recordStartAt.current = Date.now();
    qStarts.current = [0]; // Q0 starts at offset 0
    setRecording(true);
  };
  const stopRecording = () => {
    if (mediaRecorder.current && recording) mediaRecorder.current.stop();
    setRecording(false);
  };

  const nextQuestion = () => {
    if (qIdx + 1 < data.questions.length) setQIdx(qIdx + 1);
    else uploadAll();
  };

  const uploadAll = async () => {
    setUploading(true);
    try {
      const blob = new Blob(chunks.current, { type: "video/webm" });
      const fd = new FormData();
      fd.append("file", blob, `interview-${inviteId}.webm`);
      // Backfill any missing entries so length matches the question count
      while (qStarts.current.length < data.questions.length) qStarts.current.push(null);
      fd.append("question_offsets", JSON.stringify(qStarts.current));
      const res = await fetch(`${API}/interview/${inviteId}/submit`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      setStep("done"); toast.success("Submitted successfully!");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  if (!data) return <div className="min-h-screen grid place-items-center">Loading interview…</div>;

  const q = data.questions[qIdx];
  const progress = ((qIdx + (recorded ? 1 : 0)) / data.questions.length) * 100;
  // Trim any trailing period on the company name to avoid "Co.." when the sentence ends with a period.
  const company = String(data.job.company || "").replace(/\.+\s*$/, "");
  const totalMinutes = Math.max(
    3,
    Math.round(
      (data.questions || []).reduce((s, x) => s + (x.think_time || 0) + (x.answer_time || 0), 0) / 60
    )
  );

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col" data-testid="candidate-portal">
      <div className="hero-gradient relative">
        <div className="grain" />
        <div className="relative max-w-4xl mx-auto px-6 py-6">
          <Logo variant="wordmark-white" size={28} />
        </div>
      </div>
      <div className="max-w-4xl mx-auto w-full px-6 pt-8 pb-16 flex-1">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            {step === "welcome" && (
              <div data-testid="portal-welcome">
                <div className="text-xs uppercase tracking-widest text-pink font-semibold">Interview invitation</div>
                <h1 className="text-3xl font-bold font-display mt-2">Welcome, {data.candidate.name}</h1>
                <p className="text-neutral-600 mt-2">
                  You're invited to complete a video interview for{" "}
                  <span className="font-semibold text-neutral-900">{data.job.title}</span>
                  {company ? <> at <span className="font-semibold text-neutral-900">{company}</span></> : null}.
                </p>
                <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg bg-neutral-50 p-4 border border-neutral-100"><Video className="w-4 h-4 text-pink mb-2" /><div className="font-semibold">{data.questions.length} questions</div><div className="text-neutral-500">One-way format</div></div>
                  <div className="rounded-lg bg-neutral-50 p-4 border border-neutral-100"><Clock className="w-4 h-4 text-pink mb-2" /><div className="font-semibold">~{totalMinutes} minutes</div><div className="text-neutral-500">Total time</div></div>
                  <div className="rounded-lg bg-neutral-50 p-4 border border-neutral-100"><Camera className="w-4 h-4 text-pink mb-2" /><div className="font-semibold">Camera + mic</div><div className="text-neutral-500">Required</div></div>
                </div>

                <div className="mt-8">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5 text-pink" /> What to expect
                  </div>
                  <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-neutral-700">
                    <li className="flex gap-2"><span className="text-pink font-bold">01</span> Each question shows a short think-time countdown before recording starts automatically.</li>
                    <li className="flex gap-2"><span className="text-pink font-bold">02</span> Speak as if you're chatting with a colleague — natural is best.</li>
                    <li className="flex gap-2"><span className="text-pink font-bold">03</span> A quiet, well-lit spot with a stable connection works best.</li>
                    <li className="flex gap-2"><span className="text-pink font-bold">04</span> Once submitted, the recruiter will review and get back to you.</li>
                  </ul>
                </div>

                <div className="mt-8 flex items-center gap-4 flex-wrap">
                  <Button className="btn-primary rounded-full" onClick={() => setStep("setup")} data-testid="start-setup-btn">
                    Continue to setup
                  </Button>
                  <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-teal" /> Your recording is encrypted and only visible to the hiring team.
                  </div>
                </div>
              </div>
            )}
            {step === "setup" && (
              <div data-testid="portal-setup">
                <h1 className="text-2xl font-bold">Hardware check</h1>
                <p className="text-neutral-600 mt-1">Grant camera and microphone permissions.</p>
                <video ref={videoRef} autoPlay playsInline className="w-full mt-4 rounded-lg bg-black aspect-video" />
                <Button className="btn-primary rounded-full mt-6" disabled={!stream} onClick={() => setStep("interview")} data-testid="start-interview-btn">
                  I'm ready — start interview
                </Button>
              </div>
            )}
            {step === "interview" && (
              <div data-testid="portal-interview">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-neutral-500">Question {qIdx + 1} of {data.questions.length}</div>
                  <Badge variant="outline">{recording ? `Recording · ${answerLeft}s` : thinkLeft > 0 ? `Think · ${thinkLeft}s` : "Ready"}</Badge>
                </div>
                <Progress value={progress} className="mt-2" />
                <h2 className="text-xl font-semibold mt-4">{q.text}</h2>
                <video ref={videoRef} autoPlay playsInline className="w-full mt-4 rounded-lg bg-black aspect-video" />
                <div className="mt-4 flex gap-2">
                  {!recording && !recorded && thinkLeft > 0 && <Button className="btn-primary rounded-full" onClick={() => { setThinkLeft(0); }} data-testid="skip-think-btn">Skip think time</Button>}
                  {recording && <Button className="rounded-full" variant="destructive" onClick={stopRecording} data-testid="stop-record-btn">Stop recording</Button>}
                  {recorded && <Button className="btn-primary rounded-full" onClick={nextQuestion} disabled={uploading} data-testid="next-question-btn">
                    {uploading ? "Submitting…" : (qIdx + 1 < data.questions.length ? "Next question" : "Submit interview")}
                  </Button>}
                </div>
              </div>
            )}
            {step === "done" && (
              <div className="py-6" data-testid="portal-done">
                <div className="text-center">
                  <div className="relative inline-block">
                    <div className="w-16 h-16 rounded-full bg-teal/10 grid place-items-center mx-auto">
                      <CheckCircle2 className="w-10 h-10 text-teal" />
                    </div>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-teal font-semibold mt-4">Interview submitted</div>
                  <h1 className="text-3xl font-bold font-display mt-1">All done, {data.candidate.name.split(" ")[0]} 🎉</h1>
                  <p className="text-neutral-600 mt-2 max-w-md mx-auto">
                    Your responses are safely saved and encrypted. Here's what happens next.
                  </p>
                </div>

                {/* What happens next timeline */}
                <div className="mt-8 max-w-lg mx-auto" data-testid="portal-next-steps">
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-3 text-center">What happens next</div>
                  <ol className="space-y-3">
                    {[
                      {
                        icon: Sparkles,
                        eta: "Within an hour",
                        title: "Alfred reviews your responses",
                        body: "Our AI transcribes each answer and drafts a short summary for the recruiter.",
                      },
                      {
                        icon: Eye,
                        eta: "1–3 business days",
                        title: `The ${company || "hiring"} team watches your interview`,
                        body: "Real humans — not just the AI — evaluate your fit for the role.",
                      },
                      {
                        icon: Inbox,
                        eta: "Within 5 business days",
                        title: "You'll hear back — either way",
                        body: "We'll email you with next steps whether you move forward or not. Please check your inbox (and spam folder just in case).",
                      },
                    ].map((s, i) => (
                      <li key={i} className="flex gap-3 rounded-xl border border-neutral-200 bg-white p-3.5" data-testid={`portal-step-${i}`}>
                        <div className="w-9 h-9 rounded-full bg-pink/10 grid place-items-center shrink-0">
                          <s.icon className="w-4 h-4 text-pink" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-widest text-pink font-semibold">{s.eta}</div>
                          <div className="text-sm font-semibold text-neutral-900 mt-0.5">{s.title}</div>
                          <div className="text-xs text-neutral-600 mt-0.5 leading-relaxed">{s.body}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Safe-to-close reassurance + CTAs */}
                <div className="mt-8 max-w-lg mx-auto rounded-xl bg-teal/5 border border-teal/20 p-4 flex items-start gap-3" data-testid="portal-safe-close">
                  <ShieldCheck className="w-5 h-5 text-teal shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-neutral-900">It's safe to close this window.</div>
                    <div className="text-xs text-neutral-600 mt-1">
                      Your submission is saved on our servers. You don't need to keep this tab open — we'll email you at the address on your invite with any updates.
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-center flex-wrap">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => { try { window.close(); } catch(e){} window.location.href = "/"; }}
                    data-testid="portal-close-btn">
                    <Home className="w-3.5 h-3.5 mr-1.5" /> Return to homepage
                  </Button>
                </div>

                <PulseWidget
                  inviteId={inviteId}
                  alreadySubmitted={data.pulse_submitted}
                />
              </div>
            )}
          </CardContent>
        </Card>
        <div className="mt-6 flex items-center justify-between text-[11px] text-neutral-400 px-1 flex-wrap gap-2" data-testid="portal-footer">
          <div className="flex items-center gap-1.5">
            <Mail className="w-3 h-3" />
            Trouble with your camera or mic? Reply to your invite email and we'll help.
          </div>
          <div>Powered by alfredHR · Your data is protected under GDPR &amp; SOC 2.</div>
        </div>
      </div>
    </div>
  );
}

function PulseWidget({ inviteId, alreadySubmitted }) {
  const [mood, setMood] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(!!alreadySubmitted);
  const MOODS = [
    { key: "happy", emoji: "😊", label: "Great" },
    { key: "neutral", emoji: "😐", label: "Okay" },
    { key: "sad", emoji: "☹️", label: "Rough" },
  ];

  const submit = async (selected) => {
    setSubmitting(true);
    try {
      await api.post(`/interview/${inviteId}/pulse`, {
        mood: selected,
        comment: comment.trim() || null,
      });
      setDone(true);
      toast.success("Thanks — your feedback helps us improve");
    } catch (e) {
      if (e.response?.status === 409) {
        setDone(true);
        toast.info("You've already shared your feedback for this interview");
      } else {
        toast.error(e.response?.data?.detail || "Couldn't send your feedback");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-8 rounded-xl border border-teal/20 bg-teal/5 p-5 text-center"
        data-testid="pulse-widget-done">
        <div className="text-2xl">🙏</div>
        <div className="text-sm font-semibold text-neutral-800 mt-1">Feedback received</div>
        <div className="text-xs text-neutral-500 mt-1">
          Thanks for taking a moment. Candid input like yours helps us make hiring better for everyone.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-xl border border-neutral-200 bg-neutral-50 p-5" data-testid="pulse-widget">
      <div className="text-xs uppercase tracking-widest text-pink font-semibold text-center">Quick pulse · 15 seconds</div>
      <div className="text-base font-semibold text-neutral-900 text-center mt-1">
        How did that go for you?
      </div>
      <div className="text-xs text-neutral-500 text-center mt-1">
        Your answer stays anonymous in our reports — we only use it to improve the process.
      </div>

      <div className="mt-4 flex items-center justify-center gap-3" data-testid="pulse-mood-picker">
        {MOODS.map(m => {
          const selected = mood === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMood(m.key)}
              disabled={submitting}
              data-testid={`pulse-mood-${m.key}`}
              className={`flex flex-col items-center justify-center w-20 h-20 rounded-2xl border-2 transition-all ${
                selected
                  ? "border-pink bg-pink/10 scale-105 shadow-md"
                  : "border-neutral-200 bg-white hover:border-pink/40 hover:scale-105"
              }`}>
              <div className="text-3xl leading-none">{m.emoji}</div>
              <div className={`text-[11px] mt-1 whitespace-nowrap ${selected ? "font-semibold text-pink" : "text-neutral-600"}`}>{m.label}</div>
            </button>
          );
        })}
      </div>

      {mood && (
        <div className="mt-4 animate-in fade-in duration-300" data-testid="pulse-comment-section">
          <label className="text-xs text-neutral-500">
            Anything you'd like the team to know? (optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            rows={2}
            className="w-full mt-1.5 px-3 py-2 text-sm rounded-lg border border-neutral-200 focus:border-pink focus:outline-none focus:ring-2 focus:ring-pink/20"
            placeholder={
              mood === "happy" ? "What worked well?"
                : mood === "sad" ? "What made it rough? We'd love to fix it."
                : "Any thoughts on how we could improve?"
            }
            data-testid="pulse-comment-text"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] text-neutral-400">{comment.length}/500</div>
            <Button
              className="btn-primary rounded-full text-xs h-8"
              disabled={submitting}
              onClick={() => submit(mood)}
              data-testid="pulse-submit-btn">
              {submitting ? "Sending…" : "Send feedback"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
